import React, { useState, useEffect } from 'react';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { initializeStripeConnect } from '../stripeConnect';
import { CircularProgress, Box, Alert } from '@mui/material';

const StripeConnectWrapper = ({ children }) => {
  const [stripeConnect, setStripeConnect] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false - don't load by default
  const [error, setError] = useState(null);

  // Don't initialize Stripe Connect automatically on app startup
  // Only initialize when actually needed for Stripe functionality
  const initializeStripeIfNeeded = async () => {
    if (stripeConnect || loading) return stripeConnect;
    
    try {
      setLoading(true);
      console.log('🔄 Initializing Stripe Connect...');
      const connectInstance = await initializeStripeConnect();
      setStripeConnect(connectInstance);
      setError(null);
      console.log('✅ Stripe Connect initialized successfully');
      return connectInstance;
    } catch (err) {
      console.error('❌ Failed to initialize Stripe Connect:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Make the initialization function available to child components
  useEffect(() => {
    // Attach the initialization function to window for access by components that need Stripe
    window.initializeStripeConnect = initializeStripeIfNeeded;
    
    return () => {
      delete window.initializeStripeConnect;
    };
  }, [stripeConnect, loading]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <div>Initializing Stripe Connect...</div>
      </Box>
    );
  }

  if (error) {
    console.warn('Stripe Connect initialization failed, continuing without Stripe:', error);
    // Don't show error to user - just log it and continue
    return children;
  }

  if (!stripeConnect) {
    // Don't show any loading or error - just render children normally
    // Stripe features will initialize when needed
    return children;
  }

  return (
    <ConnectComponentsProvider connectInstance={stripeConnect}>
      {children}
    </ConnectComponentsProvider>
  );
};

export default StripeConnectWrapper;